/**
 * CounterpointAnalysis.js
 * Analyzes a generated melody track to provide context for counter-lines and bass.
 * Calculates Activity (Rhythmic Density) and Contour (Direction).
 */

export const analyzeMelody = (melodyEvents, totalBars = 4) => {
    if (!melodyEvents || melodyEvents.length === 0) {
        return {
            activityMap: new Array(totalBars * 4).fill(0), // Per Beat
            contourMap: new Array(totalBars).fill(0), // Per Bar
            registerMap: new Array(totalBars).fill({ min: 60, max: 72 })
        };
    }

    // 1. Activity Map (Density per Beat)
    // We assume 4 beats per bar usually.
    const activityMap = new Array(totalBars * 4).fill(0);

    melodyEvents.forEach(ev => {
        const beatIndex = Math.floor(ev.s);
        if (beatIndex >= 0 && beatIndex < activityMap.length) {
            // Add density score based on note duration
            // Short notes = Higher Activity
            // 16th (0.25) -> +0.5
            // 8th (0.5) -> +0.25
            // Quarter (1.0) -> +0.1
            const score = (ev.len < 0.3) ? 0.5 : (ev.len < 0.6 ? 0.25 : 0.1);
            activityMap[beatIndex] += score;
        }
    });

    // Normalize Activity (Clamp 0..1)
    for(let i=0; i<activityMap.length; i++) {
        activityMap[i] = Math.min(1.0, activityMap[i]);
    }

    // 2. Contour & Register Map (Per Bar)
    const contourMap = new Array(totalBars).fill(0); // -1 (Down), 0 (Flat), 1 (Up)
    const registerMap = [];

    for (let b = 0; b < totalBars; b++) {
        const barStart = b * 4;
        const barEnd = barStart + 4;

        // Filter events in this bar
        const barEvents = melodyEvents.filter(ev => ev.s >= barStart && ev.s < barEnd);

        if (barEvents.length < 2) {
            contourMap[b] = 0;
            const pitch = barEvents.length > 0 ? barEvents[0].d : 60;
            registerMap.push({ min: pitch, max: pitch, avg: pitch });
            continue;
        }

        // Sort by time
        barEvents.sort((a,b) => a.s - b.s);

        // Calculate Contour
        const startPitch = barEvents[0].d;
        const endPitch = barEvents[barEvents.length-1].d;
        const diff = endPitch - startPitch;

        if (diff > 2) contourMap[b] = 1; // Ascending
        else if (diff < -2) contourMap[b] = -1; // Descending
        else contourMap[b] = 0; // Flat/Static

        // Calculate Register
        let min = Infinity;
        let max = -Infinity;
        let sum = 0;

        barEvents.forEach(e => {
            if (e.d < min) min = e.d;
            if (e.d > max) max = e.d;
            sum += e.d;
        });

        registerMap.push({
            min: min,
            max: max,
            avg: sum / barEvents.length
        });
    }

    return {
        activityMap,
        contourMap,
        registerMap
    };
};
